'use no memo';

import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import { ProgressCircle } from '@/components/Progress';
import { TooltipWrapper } from '@/components/Tooltip/TooltipWrapper';
import { Text } from '@/components/Typography/Text';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { operationInstanceQueryKeys } from '@/features/operation-instances/hooks';
import { StepCard } from '@/features/operation-package-instance-profile/-components/StepCard';
import { OperationPackageInstanceTargetLink } from '@/features/operation-package-instance/-components/OperationPackageInstanceTargetLink';
import { LabelPerson } from '@/features/person/components/LabelPerson';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { useTaskOperation } from './-hooks/useTaskOperation';
import { useTodoAction } from './-hooks/useTodoAction';
// --- マイナンバー収集モック（暫定・削除しやすいよう本 import と下記ブロックに集約）---
import { MYNUMBER_MOCK_STEP, MYNUMBER_MOCK_STEP_ACTIVE, MockAwareProgressCircle, MyNumberMockProvider, MyNumberMockStepCard, isMyNumberMockEnabled } from './-mynumber-mock';
import { useQueryOperationPackageInstanceProgress, useSuspenseQueryOperationPackageInstance, usePostOperationPackageInstanceApprove } from './-queries';
import { TaskStatus } from './-types/operation-package';
export const Route = createFileRoute('/_staff/labor-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/_profile/')({
  component: RouteComponent
});
function RouteComponent() {
  const {
    t
  } = useTranslation(defaultNS);
  const {
    instanceId
  } = Route.useParams();
  const {
    data
  } = useSuspenseQueryOperationPackageInstance(instanceId);
  const {
    data: progressData
  } = useQueryOperationPackageInstanceProgress(instanceId);
  const {
    toast
  } = useToast();
  const todoAction = useTodoAction();
  const queryClient = useQueryClient();
  const handleAdminMutationSuccess = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: operationInstanceQueryKeys.detail('HRF_LABOR_CORE', instanceId)
    });
  }, [queryClient, instanceId]);
  const completedCount = progressData?.completedCount ?? 0;
  const totalCount = progressData?.totalCount ?? 0;

  // --- マイナンバー収集モック（暫定・削除時はこのブロックと下の分岐/import を消す）---
  // フラグ有効時、個人情報収集ステップ直後（index 1）へモックステップを 1 枚強引に差し込む。
  const mockEnabled = isMyNumberMockEnabled(data.packageType);
  const mockInsertIndex = Math.min(1, data.operationSteps.length);
  const renderSteps: Array<{
    kind: 'real';
    step: (typeof data.operationSteps)[number];
  } | {
    kind: 'mock';
  }> = data.operationSteps.map(step => ({
    kind: 'real',
    step
  }));
  if (mockEnabled) {
    renderSteps.splice(mockInsertIndex, 0, {
      kind: 'mock'
    });
  }
  // 直前の実ステップが完了していれば、モックを active（緑）として繋げる
  // （フロー上「現在のステップ」に見せて、グレーの gap をなくす）。
  // 完了判定は step レベルの status を使う（backend が次ステップ開始のゲートに使う信号と同じ。
  // タスク status の集計より正確で、Skipped/Abort や「作業済み・確認待ち」を誤判定しない）。
  const stepBeforeMock = mockInsertIndex > 0 ? data.operationSteps[mockInsertIndex - 1] : undefined;
  const mockStepActive = mockEnabled && stepBeforeMock?.status === TaskStatus.Completed;

  // 承認待ちタスクがあるかどうかをチェック
  const hasWaitingApprovalTasks = data.operationSteps.some(step => step.operationTaskInstances.some(task => task.status === TaskStatus.WaitingApproval));

  // 承認待ちのタスクIDを取得
  const waitingApprovalTaskIds = data.operationSteps.flatMap(step => step.operationTaskInstances).filter(task => task.status === TaskStatus.WaitingApproval).map(task => task.id);

  // インスタンスのステータスによってアクションの可否を判定
  const canApprove = data.status === TaskStatus.InProgress && hasWaitingApprovalTasks;

  // 完了処理用
  const {
    mutate: approveInstance,
    isPending: isApprovePending
  } = usePostOperationPackageInstanceApprove(instanceId, waitingApprovalTaskIds, {
    onSuccess: () => {
      toast({
        description: t('features.operationPackageInstance.action.approveAllSuccess')
      });
    },
    onError: message => {
      toast({
        variant: 'destructive',
        description: message
      });
    }
  });
  const content = <HStack className="px-12 py-7 gap-9 h-full">
      <VStack className="w-full border rounded-lg p-12 gap-3 shadow-xs bg-white overflow-auto">
        <HStack className="justify-between items-center">
          <Text variant="h2">{data.name}</Text>
          <TooltipWrapper content={!canApprove ? t('features.operationPackageInstance.action.cannotApproveAll') : undefined} side="top">
            <ActionButton variant="secondary" onClick={() => approveInstance()} isLoading={isApprovePending} disabled={!canApprove}>
              {t('features.operationPackageInstance.action.approveAll')}
            </ActionButton>
          </TooltipWrapper>
        </HStack>
        {data.description && <Text>{data.description}</Text>}
        <HStack className="border-2 rounded-md">
          <OperationPackageInstanceTargetLink target={data.target} />
          <Separator orientation="vertical" />
          <VStack className="p-4 gap-2">
            <Text variant="labelSmall" className="text-neutral-muted-on-fill">
              {t('pages.laborCore.instance.assignee')}
            </Text>
            <Link to="/person/$id" params={{
            id: data.operator.operatorId
          }}>
              <LabelPerson name={data.operator.operatorName} avatarUrl={data.operator.operatorAvatarUrl} size="Default" hoverScale={false} />
            </Link>
          </VStack>
        </HStack>
        <VStack className="gap-4">
          {renderSteps.map((rs, index) => {
          const stepNumber = index + 1;
          const isLastStep = index === renderSteps.length - 1;
          const next = renderSteps[index + 1];
          const nextStep = !next ? undefined : next.kind === 'real' ? next.step : mockStepActive ? MYNUMBER_MOCK_STEP_ACTIVE : MYNUMBER_MOCK_STEP;
          if (rs.kind === 'mock') {
            return <MyNumberMockStepCard key="mynumber-mock" stepNumber={stepNumber} isLastStep={isLastStep} nextStep={nextStep} instanceTarget={data.target} forceStepActive={mockStepActive} />;
          }
          return <StepCard key={rs.step.id} step={rs.step} stepNumber={stepNumber} isLastStep={isLastStep} nextStep={nextStep} isPackageAbort={data.status === 'Aborted'} useTaskOperation={useTaskOperation} todoAction={todoAction} instanceTarget={data.target} designationContext={{
            allSteps: data.operationSteps
          }} enableAdminActions onAdminMutationSuccess={handleAdminMutationSuccess} />;
        })}
        </VStack>
      </VStack>

      <div className="h-fit hidden lg:block">
        {mockEnabled ?
      // マイナンバー収集モック分（3 タスク）を進捗に加算（MyNumberMockProvider 配下）
      <MockAwareProgressCircle baseCurrent={completedCount} baseTotal={totalCount} title={t('pages.laborCore.instance.progress.title')} /> : <ProgressCircle current={completedCount} total={totalCount} title={t('pages.laborCore.instance.progress.title')} expandable={false} />}
      </div>
    </HStack>;
  return <FillScreen>
      {mockEnabled ?
    // マイナンバー収集モック: ステップ本文と進捗加算で状態（入力スキップ）を共有する
    <MyNumberMockProvider>{content}</MyNumberMockProvider> : content}
    </FillScreen>;
}